/**
 * calendar-filter — 캘린더 상태 칩 토글(client-side).
 *
 * [data-status-filter] 안의 [data-status-toggle="{status|all}"] 칩 클릭 시,
 * 연결된 캘린더(.ui-calendar)의 칩(.ui-calendar__chip--{status}) 가시성을 토글한다.
 * 컨트롤러·board-calendar 변경 없이 기존 chipClass 만으로 동작 (#19).
 *
 * 마크업 계약:
 *   <div data-status-filter data-calendar-target="#cal-id">
 *     <button data-status-toggle="all" class="...is-active">전체</button>
 *     <button data-status-toggle="pending" class="...is-active">Pending</button>
 *     ...
 *   </div>
 *   <div class="ui-calendar" id="cal-id"> ... .ui-calendar__chip--pending ... </div>
 *
 * data-calendar-target 미지정 시: 같은 부모 트리에서 가장 가까운 .ui-calendar 를 찾는다.
 */
export function initAdminCalendarFilter(root: ParentNode = document): void {
  const filters = (root as Element | Document).querySelectorAll<HTMLElement>(
    "[data-status-filter]:not([data-status-filter-inited])",
  );
  filters.forEach((filter) => {
    filter.dataset.statusFilterInited = "1";
    bindFilter(filter);
  });
}

function resolveCalendar(filter: HTMLElement): HTMLElement | null {
  const sel = filter.getAttribute("data-calendar-target");
  if (sel) return document.querySelector<HTMLElement>(sel);
  // 같은 섹션/페이지에서 가장 가까운 캘린더
  const scope = filter.closest(".admin-page, body") ?? document;
  return scope.querySelector<HTMLElement>(".ui-calendar");
}

function bindFilter(filter: HTMLElement): void {
  const calendar = resolveCalendar(filter);
  if (!calendar) return;

  const toggles = Array.from(
    filter.querySelectorAll<HTMLElement>("[data-status-toggle]"),
  );
  // 알려진 상태 목록 = 'all' 제외한 토글들
  const statuses = toggles
    .map((t) => t.getAttribute("data-status-toggle") ?? "")
    .filter((s) => s !== "" && s !== "all");

  const apply = (): void => {
    const allBtn = toggles.find((t) => t.getAttribute("data-status-toggle") === "all");
    const active = new Set(
      toggles
        .filter((t) => t.getAttribute("data-status-toggle") !== "all" && t.classList.contains("is-active"))
        .map((t) => t.getAttribute("data-status-toggle") ?? ""),
    );
    // 전체 버튼 활성 = 모든 상태 활성(또는 아무 상태도 명시 활성 아님)
    const allOn = active.size === 0 || active.size === statuses.length;
    if (allBtn) {
      allBtn.classList.toggle("is-active", allOn);
      allBtn.setAttribute("aria-pressed", allOn ? "true" : "false");
    }
    statuses.forEach((status) => {
      const visible = allOn || active.has(status);
      calendar
        .querySelectorAll<HTMLElement>(`.ui-calendar__chip--${status}`)
        .forEach((chip) => chip.toggleAttribute("hidden", !visible));
    });
  };

  toggles.forEach((toggle) => {
    toggle.addEventListener("click", () => {
      const key = toggle.getAttribute("data-status-toggle") ?? "";
      if (key === "all") {
        // 전체 = 모든 상태 토글 활성으로 리셋
        toggles.forEach((t) => {
          if (t.getAttribute("data-status-toggle") !== "all") {
            t.classList.add("is-active");
            t.setAttribute("aria-pressed", "true");
          }
        });
      } else {
        toggle.classList.toggle("is-active");
        toggle.setAttribute("aria-pressed", toggle.classList.contains("is-active") ? "true" : "false");
      }
      apply();
    });
  });

  apply();
}
